import React, { Component, ReactNode } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { ConnectedProps, connect } from 'react-redux';
import YesNoDialog from '../components/YesNoDialog/YesNoDialog';
import { Segment } from '../core/core';
import { postServiceRequest } from '../serviceAgent';
import { getAppRoot } from '../identity';
import { isParentsEmailSet } from './UtilityFunctions';
import ConnectParentInvitationList from './ConnectParentInvitationList';
import { dismissForever, dismissTemporarily, isShouldShow } from '../core/core';
import { SpinnerOverlay } from '../SpinnerOverlay';
import { selectClassroom, updateClassroom } from '../Classrooms';
import PrimaryButton from '../components/PrimaryButton/PrimaryButton';
import styles from '../SCSS/Students.module.scss';
import utilityStyles from '../SCSS/common/utilityStyles.module.scss';
import segmentStyles from '../SCSS/Segment.module.scss';
import { getNewParentUid, getUsersEmail } from '../GlobalFunctions';
import {
  getAuthorizationToken,
  updateClassroom as updateClassroomBackend,
  generateConsentRequests as generateConsentRequestsBackend,
  inviteParent,
} from '../BackendInterface';
import { AppDispatch, RootState, getStore } from '../ApplicationState';
import { useErrorStatus } from '../components/EnableErrorMessagePopup/EnableErrorMessagePopup';
import {
  ClassroomType,
  ConsentRequest,
  InviteParentConnect,
  StudentProxyType,
} from '@peekapak/types/DataProtocolTypes';
import { History, Location } from 'history';
import { StudentRequestType } from './Students';

type Props = PropsFromRedux & {
  isAuthenticated: boolean;
  history: History;
  location: Location;
  loginState: string;
  onShowErrorMessage: (message: string) => void;
  onShowGetStartedDialog: () => void;
  params: {};
};

interface State {
  isShowDetailsModal: boolean;
  isEditingParentInvitations: boolean;
  isSendInviteError: boolean;
  isTemporarilyAllowResend: boolean;
  isShowSpinner: boolean;
  sendInviteErrorSid: string | undefined;
  classroomUnderEdit: ClassroomType;
  classroomUnderEditIndex: number;
  isShowHint: boolean;
  invitationSendPendingList: string[];
  isShowParentEmailChangeModal: boolean;
  isShowSendAllModal: boolean;
  studentUnderEdit?: StudentProxyType;
  parentsEmail?: string;
  continueChangeParentEmail?: boolean;
  isSendingAllEmails?: boolean;
}
class ConnectParents extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const editableClassroom = { ...props.classroom };

    this.state = {
      isShowDetailsModal: false,
      isEditingParentInvitations: false,
      isSendInviteError: false,
      isTemporarilyAllowResend: false,
      isShowSpinner: false,
      sendInviteErrorSid: undefined,
      classroomUnderEdit: editableClassroom,
      classroomUnderEditIndex: props.classrooms.selectedClassroom,
      isShowHint: this.isShouldShowHint(props),
      invitationSendPendingList: [],
      isShowParentEmailChangeModal: false,
      isShowSendAllModal: false,
    };

    this.handleOpenInviteParents = this.handleOpenInviteParents.bind(this);
    this.handleSetParentsEmail = this.handleSetParentsEmail.bind(this);
    this.handleOnInvite = this.handleOnInvite.bind(this);
    this.handleOnResetError = this.handleOnResetError.bind(this);
    this.handleTemporarilyAllowResend = this.handleTemporarilyAllowResend.bind(this);
    this.hideHint = this.hideHint.bind(this);
    this.hideHintForSession = this.hideHintForSession.bind(this);
    this.hideHintForever = this.hideHintForever.bind(this);
    this.showHint = this.showHint.bind(this);
    this.hideHintForever = this.hideHintForever.bind(this);
    this.isShouldShowHint = this.isShouldShowHint.bind(this);
    this.handleSendAllEmails = this.handleSendAllEmails.bind(this);
    this.handleOnHideParentEmailChangeModal = this.handleOnHideParentEmailChangeModal.bind(this);
    this.handleOnOpenParentEmailChangeModal = this.handleOnOpenParentEmailChangeModal.bind(this);
    this.handleSetParentsEmailPart2 = this.handleSetParentsEmailPart2.bind(this);
    this.handleOnOpenSendAllModal = this.handleOnOpenSendAllModal.bind(this);
    this.handleOnHideSendAllModal = this.handleOnHideSendAllModal.bind(this);
    this.handleSendAllEmailsPart2 = this.handleSendAllEmailsPart2.bind(this);
    this.generateConsentItems = this.generateConsentItems.bind(this);
    this.handleOnYesResponse = this.handleOnYesResponse.bind(this);
  }

  sendParentInvitationSignal = (isSending: boolean) => {
    this.setState({
      isShowSpinner: isSending,
    });
  };

  handleOnHideSendAllModal() {
    this.setState({
      isShowSendAllModal: false,
    });
  }

  handleOnOpenSendAllModal() {
    this.setState({
      isShowSendAllModal: true,
    });
  }

  handleOnOpenParentEmailChangeModal() {
    this.setState({
      isShowParentEmailChangeModal: true,
    });
  }

  handleOnHideParentEmailChangeModal() {
    this.setState({
      isShowParentEmailChangeModal: false,
    });
  }

  isShouldShowHint(props: Props) {
    const { classroom } = props;
    if (!classroom) return false;
    const studentList = classroom.listOfStudentProxies;
    const numberOfParentEmails = this.countParentEmails(studentList);

    if (studentList && numberOfParentEmails < 1 && props.userProfile) {
      return isShouldShow(props.userProfile, 'ConnectParentsHint');
    }

    return false;
  }

  hideHint() {
    this.setState({
      isShowHint: false,
    });
  }

  hideHintForever() {
    this.hideHint();
    dismissForever('ConnectParentsHint', this.props.userProfile?.flags);
  }

  hideHintForSession() {
    this.hideHint();
    dismissTemporarily('ConnectParentsHint');
  }

  showHint() {
    this.setState({
      isShowHint: true,
    });
  }

  handleTemporarilyAllowResend(event: Event) {
    event.preventDefault();

    this.setState({
      isTemporarilyAllowResend: true,
    });
  }

  handleOnResetError(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    this.setState({
      isSendInviteError: false,
      sendInviteErrorSid: undefined,
    });
  }

  handleOpenInviteParents(event: React.MouseEvent) {
    event.preventDefault();

    this.setState({
      isShowDetailsModal: true,
      isEditingParentInvitations: true,
    });
  }

  async handleSetParentsEmail(
    studentUnderEdit: StudentProxyType,
    parentsEmail: string,
  ): Promise<StudentProxyType | null> {
    const storedClassroom = this.props.classrooms.list[this.state.classroomUnderEditIndex];
    const classroomUnderEdit = this.state.classroomUnderEdit;
    const storedStudentIndex = storedClassroom.listOfStudentProxies.findIndex(
      (student) => studentUnderEdit.sid === student.sid,
    );
    const storedStudent = storedClassroom.listOfStudentProxies[storedStudentIndex];
    const storedParentsEmail = storedStudent?.listOfParentProxies?.[0]?.email || '';

    this.setState({
      studentUnderEdit: studentUnderEdit,
      parentsEmail: parentsEmail,
    });

    // if stored parents email is the same as the new one, don't do anything
    if (storedParentsEmail === parentsEmail) {
      return storedStudent;
    }

    //only change email if it's different from the existing one
    if (storedParentsEmail && storedParentsEmail !== parentsEmail) {
      this.handleOnOpenParentEmailChangeModal();
      return storedStudent;
    } else {
      //
      // if there is no existing parent email, just set it
      //
      this.setState({
        continueChangeParentEmail: true,
      });

      const studentClone = cloneDeep(studentUnderEdit);

      if (studentClone.listOfParentProxies === undefined) {
        studentClone.listOfParentProxies = [];
      }

      if (studentClone.listOfParentProxies.length === 0) {
        const newParentId = getNewParentUid(studentClone.sid);
        const newParent = {
          pid: newParentId,
          email: '',
        };

        studentClone.listOfParentProxies.push(newParent);
      } else {
        if (studentClone.listOfParentProxies[0].invitationStatus) {
          delete studentClone.listOfParentProxies[0].invitationStatus;
        }
      }

      const classroomClone = cloneDeep(classroomUnderEdit);
      studentClone.listOfParentProxies[0].email = parentsEmail;
      classroomClone.listOfStudentProxies[storedStudentIndex] = studentClone;
      try {
        const result = await updateClassroomBackend(
          classroomClone.email,
          classroomClone.className,
          classroomClone,
          true,
        );
        this.props.updateClassroom(result);
        return studentClone;
      } catch (error: any) {
        console.error('Student::handleSetParentsEmail - postServiceRequest failed: ', error);
        this.props.onShowErrorMessage(`Student::handleSetParentsEmail - postServiceRequest failed: ${error.message}`);
        return null;
      }
    }
  }

  async handleSetParentsEmailPart2(): Promise<StudentProxyType | null> {
    const studentUnderEdit = this.state.studentUnderEdit;
    const parentsEmail = this.state.parentsEmail;

    const storedClassroom = this.props.classrooms.list[this.state.classroomUnderEditIndex];
    const classroomUnderEdit = this.state.classroomUnderEdit;
    const storedStudentIndex = storedClassroom.listOfStudentProxies.findIndex(
      (student) => studentUnderEdit?.sid === student.sid,
    );
    const storedStudent = storedClassroom.listOfStudentProxies[storedStudentIndex];
    const storedParentsEmail = storedStudent?.listOfParentProxies?.[0]?.email || '';

    this.setState({
      continueChangeParentEmail: true,
      isShowParentEmailChangeModal: false,
    });
    // does the student even have a list of parents yet?
    // if the stored version doesn't have it, the under edit version
    // won't either
    const studentClone = cloneDeep(studentUnderEdit);
    if (!studentClone) {
      this.props.onShowErrorMessage('handleSetParentsEmailPart2: studentUnderEdit is undefined');
    }
    if (studentClone.listOfParentProxies === undefined) {
      studentClone.listOfParentProxies = [];
    }

    if (studentClone.listOfParentProxies.length === 0) {
      const newParentId = getNewParentUid(storedStudent.sid);
      const newParent = {
        pid: newParentId,
        email: '',
      };

      studentClone.listOfParentProxies.push(newParent);
    } else {
      if (studentClone.listOfParentProxies[0].invitationStatus) {
        delete studentClone.listOfParentProxies[0].invitationStatus;
      }
    }

    if (storedParentsEmail !== parentsEmail) {
      studentClone.listOfParentProxies[0].email = parentsEmail || '';

      const classroomClone = cloneDeep(classroomUnderEdit);
      classroomClone.listOfStudentProxies[storedStudentIndex] = studentClone;

      try {
        const result = await updateClassroomBackend(
          classroomClone.email,
          classroomClone.className,
          classroomClone,
          true,
        );
        this.props.updateClassroom(classroomClone);
        return studentClone;
      } catch (error: any) {
        console.error('Student::handleSetParentsEmail - postServiceRequest failed: ', error);
        this.props.onShowErrorMessage(`Student::handleSetParentsEmail - postServiceRequest failed: ${error.message}`);
      }
    }

    return null;
  }

  handleOnYesResponse() {
    this.handleSetParentsEmailPart2().then((student) => {
      this.setState({
        continueChangeParentEmail: false,
      });
      this.handleOnInvite(student);
    });
  }

  handleSendAllEmails() {
    const studentList = this.state.classroomUnderEdit.listOfStudentProxies;

    const studentListLength = studentList.length;
    let numberOfParentsWhoNeverResponded = 0;

    for (const student of studentList) {
      if (student.listOfParentProxies) {
        const invitationStatus = student.listOfParentProxies[0]?.invitationStatus?.status;
        const invitationSent = !!invitationStatus;
        const invitationReplied =
          invitationStatus === 'Accepted' || invitationStatus === 'Declined' || invitationStatus === 'OptedOut';
        const invitationReceived = invitationStatus === 'Opened';

        if (!invitationSent || (invitationSent && !invitationReplied && !invitationReceived)) {
          ++numberOfParentsWhoNeverResponded;
        }
      }
    }

    if (numberOfParentsWhoNeverResponded !== studentListLength) {
      this.handleOnOpenSendAllModal();
    } else {
      this.handleSendAllEmailsPart2();
    }
  }

  async generateConsentItems(
    student: StudentProxyType,
    successCallback: (resultObj: StudentRequestType[]) => void,
    errorCallBack: (errorMessage: string) => void,
  ) {
    if (!this.props.userProfile) {
      this.props.onShowErrorMessage('generateConsentItems: props.userProfile is null');
    }
    const teachersEmail = this.props.userProfile.email;
    const currentClassroom = this.state.classroomUnderEdit;
    const classroomId = currentClassroom.classroomId;

    const newRequest: ConsentRequest = {
      childsUserId: student.userId,
      classroomName: currentClassroom.className,
      teachersEmail,
      classroomId,
      webAppBaseUrl: `${window.location.origin}`,
    };

    if (student.firstName) newRequest.childsFirstName = student.firstName;
    if (student.login) newRequest.childsUsername = student.login;
    if (student.password) newRequest.childsPassword = student.password;
    if (student.listOfParentProxies && student.listOfParentProxies.length) {
      const parent = student.listOfParentProxies[0];
      newRequest.parentsPid = parent.pid;
      if (parent.email) newRequest.parentsEmail = parent.email;
    }

    const requestList = [newRequest];

    const postRequest = async (
      successCallback: (resultObj: StudentRequestType[]) => void,
      errorCallback: (errorMessage: string) => void,
    ) => {
      try {
        const results: StudentRequestType[] = await generateConsentRequestsBackend(requestList);
        successCallback(results);
      } catch (error) {
        this.props.onShowErrorMessage(`Error ${error}: Could not generate consent forms`);
        errorCallBack(`Error ${error}: Could not generate consent forms`);
      }
    };

    postRequest(successCallback, errorCallBack);
  }

  async handleSendAllEmailsPart2() {
    this.handleOnHideSendAllModal();

    this.setState({
      isSendingAllEmails: true,
    });

    this.sendParentInvitationSignal(true);

    const studentList = this.state.classroomUnderEdit.listOfStudentProxies;

    try {
      for (const student of studentList) {
        if (student.listOfParentProxies) {
          const invitationStatus = student.listOfParentProxies[0]?.invitationStatus?.status;
          const invitationSent = !!invitationStatus;
          const invitationReplied =
            invitationStatus === 'Accepted' || invitationStatus === 'Declined' || invitationStatus === 'OptedOut';
          const invitationReceived = invitationStatus === 'Opened';

          if (!invitationSent || (invitationSent && !invitationReplied && !invitationReceived)) {
            await this.handleOnInvite(student);
          }
        }
      }
    } catch (error) {
      this.props.onShowErrorMessage(
        `Student::handleSetParentsEmail - postServiceRequest failed: ${(error as Error).message}`,
      );
    } finally {
      this.sendParentInvitationSignal(false);
    }
  }

  async handleOnInvite(student: StudentProxyType): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!isParentsEmailSet(student)) {
        return resolve();
      }

      const parentsEmail = student.listOfParentProxies[0].email;
      const pid = student.listOfParentProxies[0].pid;
      const classroom = this.state.classroomUnderEdit;
      const classroomName = classroom.className;

      const userProfile = getStore().getState().user.userProfile;
      if (!userProfile) {
        this.props.onShowErrorMessage('handleOnInvite: getStore().getState().user.userProfile is null');
      }
      this.generateConsentItems(
        student,
        async (resultObj) => {
          const invitationParams: InviteParentConnect = {
            teachersEmail: userProfile.email,
            teachersFirstName: userProfile.firstName!,
            teachersLastName: userProfile.lastName!,
            teacherSalutation: userProfile.salutation!,
            className: classroomName,
            studentFirstName: student.firstName,
            studentsLogin: student.login!,
            studentsPassword: student.password!,
            classroomId: classroom.classroomId,
            sid: student.sid,
            uid: userProfile.userId,
            parentsEmail,
            pid,
            ssCode: resultObj[0].ssCode!,
            oid: resultObj[0].requestOid!,
            studentUserId: resultObj[0].childsUserId,
            webAppBaseUrl: `${window.location.origin}`,
          };

          const newPendings = [...this.state.invitationSendPendingList, pid];

          this.setState({
            invitationSendPendingList: newPendings,
          });

          try {
            await inviteParent(invitationParams);

            const timestamp = new Date().getTime();
            const invitationStatus = {
              lastSendDate: timestamp,
              status: 'Sent',
            };
            const studentClone = cloneDeep(student);
            studentClone.listOfParentProxies[0].invitationStatus = invitationStatus;

            this.setState({
              isSendingAllEmails: false,
            });

            const classroomClone = cloneDeep(classroom);
            const studentIndex = classroomClone.listOfStudentProxies.findIndex((s) => s.userId === studentClone.userId);
            classroomClone.listOfStudentProxies[studentIndex] = studentClone;

            try {
              const result = await updateClassroomBackend(
                classroomClone.email,
                classroomClone.className,
                classroomClone,
                true,
              );
              const newPendings = this.removePidFromPendingArray(pid);

              this.setState({
                invitationSendPendingList: newPendings,
              });

              this.props.updateClassroom(result);
              resolve();
            } catch (error: any) {
              console.error('Student::handleOnInvite - classroom update failed: ', error);
              reject(error);
            }
          } catch (error) {
            const newPendings = this.removePidFromPendingArray(pid);

            this.setState({
              invitationSendPendingList: newPendings,
              isSendInviteError: true,
              sendInviteErrorSid: student.sid,
              isShowSendAllModal: false,
            });

            const errorToDisplay = 'Student::handleOnInvite - postServiceRequest on invite first parent failed';
            console.error(errorToDisplay);
            reject(errorToDisplay);
          }
        },
        (errorObj) => {
          console.error(errorObj);
          reject(errorObj);
        },
      );
    });
  }

  removePidFromPendingArray(pid: string) {
    const newPendings = this.state.invitationSendPendingList.slice();
    const index = newPendings.indexOf(pid);
    if (index > -1) {
      newPendings.splice(index, 1);
    }

    return newPendings;
  }

  initializeData(props: Props) {
    const { classroom } = props;
    const editableClassroom = { ...classroom };

    this.setState({
      classroomUnderEdit: editableClassroom,
      classroomUnderEditIndex: props.classrooms.selectedClassroom,
      isShowHint: this.isShouldShowHint(props),
    });
  }

  componentDidMount() {
    this.initializeData(this.props);
  }

  componentDidUpdate(prevProps: Props, _prevState: State) {
    if (this.props.classroom !== prevProps.classroom) {
      this.initializeData(this.props);
    }
  }

  countParentEmails(studentList: StudentProxyType[]) {
    if (!studentList) {
      return 0;
    }

    let c = 0;
    studentList.forEach((student) => {
      if (isParentsEmailSet(student)) {
        c++;
      }
    });

    return c;
  }

  isMyPeekavilleClassroom(classroom: ClassroomType) {
    const studentList = classroom.listOfStudentProxies;
    if (!studentList) return false;
    for (let i = 0; i < studentList.length; ++i) {
      if (studentList[i].userId) return true;
    }
    return false;
  }

  render() {
    if (!this.props.isAuthenticated) {
      return null;
    }

    const currentClassroom = this.state.classroomUnderEdit;
    const studentList = currentClassroom.listOfStudentProxies;

    return (
      <div className={`${styles.Students} ${utilityStyles.clearfix}`}>
        <SpinnerOverlay isShow={this.state.isShowSpinner} />
        <div className={styles['spacer-30']} />
        <Segment className={utilityStyles.line}>
          <div className={styles.titleLine}>
            <div className={segmentStyles.moduleTitle}>Connect Parents</div>
            {studentList && (
              <div className={styles.oneLineButtons}>
                <PrimaryButton id='connectParentsButton' onClick={this.handleSendAllEmails}>
                  Send All Emails
                </PrimaryButton>
              </div>
            )}
          </div>
        </Segment>
        <div className={styles['spacer-30']} />

        <Segment>
          <div className={styles.connectParentsList}>
            <ConnectParentInvitationList
              isSendInviteError={this.state.isSendInviteError}
              isTemporarilyAllowResend={this.state.isTemporarilyAllowResend}
              onInvite={this.handleOnInvite}
              onResetError={this.handleOnResetError}
              onSetParentsEmail={this.handleSetParentsEmail}
              sendInviteErrorSid={this.state.sendInviteErrorSid}
              sendParentInvitationSignal={this.sendParentInvitationSignal}
              sendPendingList={this.state.invitationSendPendingList}
              studentList={studentList}
            />
          </div>
          <div className={styles['spacer-30']} />
        </Segment>
        <div className={styles['spacer-30']} />
        <div className={styles.spacer} />
        <div className={styles.spacer} />
        <YesNoDialog
          isShow={this.state.isShowParentEmailChangeModal}
          onHide={this.handleOnHideParentEmailChangeModal}
          title='Are you sure?'
          onYesResponse={this.handleOnYesResponse}
          onNoResponse={this.handleOnHideParentEmailChangeModal}
        >
          <div>
            <p>By changing the parent email, you will reset all consent and option statuses.</p>
            <p>Do you want to continue?</p>
          </div>
        </YesNoDialog>
        <YesNoDialog
          isShow={this.state.isShowSendAllModal}
          onHide={this.handleOnHideSendAllModal}
          title='Are you sure?'
          onYesResponse={this.handleSendAllEmailsPart2}
          onNoResponse={this.handleOnHideSendAllModal}
          continueButtonLabel='Continue'
          cancelButtonLabel='Cancel'
        >
          Please note that only parents who never received an email will be sent one.
        </YesNoDialog>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  classrooms: {
    state: state.classrooms.state,
    list: state.classrooms.classrooms,
    selectedClassroom: state.classrooms.selectedClassroom,
  },
  classroom: selectClassroom(state),
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateClassroom: (classroom: ClassroomType) => dispatch(updateClassroom(classroom)),
});

function ClassFunctionWrapper(props: Props) {
  const onShowErrorMessage = useErrorStatus();
  return <ConnectParents {...props} onShowErrorMessage={onShowErrorMessage} />;
}

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ClassFunctionWrapper);
